import { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Globe } from 'lucide-react';

const REGIONS = [
  { id: 'nigeria', flag: '\u{1F1F3}\u{1F1EC}', label: 'Nigeria' },
  { id: 'uk', flag: '\u{1F1EC}\u{1F1E7}', label: 'United Kingdom' },
  { id: 'international', flag: '\u{1F30D}', label: 'International' },
];

const ADMIN_ROLES = ['founder', 'admin', 'super_admin', 'admin_manager'];

export default function RegionSelector({ onSelect }) {
  const { user, setRegion } = useAuth();
  const [selecting, setSelecting] = useState(false);

  const firstName = user?.name?.split(' ')[0] || 'there';
  const isMultiRegion = ADMIN_ROLES.includes(user?.role);

  const handleSelect = async (regionId) => {
    setSelecting(true);
    await setRegion(regionId);
    onSelect(regionId);
  };

  // Single-region users go straight in
  if (!isMultiRegion) {
    return null;
  }

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F7F5F2' }} data-testid="region-selector">
      <div className="text-center max-w-md mx-auto px-6">
        {/* Logo */}
        <img
          src="https://customer-assets.emergentagent.com/job_distribution-pro-1/artifacts/44tlre5m_IMG_2568.png"
          alt="Revitalise Keratin"
          className="h-16 mx-auto mb-8"
          data-testid="region-logo"
        />

        {/* Welcome */}
        <h1
          className="text-3xl text-[#2B1F27] mb-2"
          style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
          data-testid="region-welcome"
        >
          Welcome back, {firstName}
        </h1>
        <p className="text-sm text-[#999] mb-10" style={{ fontFamily: "'Inter', sans-serif" }}>
          Select your region to continue
        </p>

        {/* Region buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          {REGIONS.map(r => (
            <button
              key={r.id}
              onClick={() => handleSelect(r.id)}
              disabled={selecting}
              className="w-full sm:w-auto min-w-[160px] bg-white px-6 py-5 flex flex-col items-center gap-2 hover:shadow-lg transition-all disabled:opacity-50"
              style={{ borderRadius: '12px', boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}
              data-testid={`region-${r.id}`}
            >
              <span className="text-3xl">{r.flag}</span>
              <span className="text-[13px] font-medium text-[#2B1F27]" style={{ fontFamily: "'Inter', sans-serif" }}>{r.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/* Globe switcher component for nav bar */
export function RegionSwitcher() {
  const { user, setRegion } = useAuth();
  const [open, setOpen] = useState(false);
  const isMultiRegion = ADMIN_ROLES.includes(user?.role);

  if (!isMultiRegion) return null;

  const currentRegion = REGIONS.find(r => r.id === user?.selected_region) || REGIONS[0];

  return (
    <div className="relative" data-testid="region-switcher">
      <button
        onClick={() => setOpen(!open)}
        className="w-8 h-8 rounded-full flex items-center justify-center border border-[#E3EAF3] hover:border-[#B99A65] transition-colors text-[#4A4A4A] hover:text-[#B99A65]"
        data-testid="region-globe-btn"
        title={`Region: ${currentRegion.label}`}
      >
        <Globe className="w-4 h-4" />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-10 z-50 bg-white border border-[#E3EAF3] py-1 w-48" style={{ borderRadius: '8px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
            {REGIONS.map(r => (
              <button
                key={r.id}
                onClick={async () => { await setRegion(r.id); setOpen(false); }}
                className={`w-full text-left px-4 py-2.5 flex items-center gap-3 text-[13px] hover:bg-[#F4F8FC] transition-colors ${
                  user?.selected_region === r.id ? 'text-[#B99A65] font-medium bg-[#B99A65]/5' : 'text-[#4A4A4A]'
                }`}
                style={{ fontFamily: "'Inter', sans-serif" }}
                data-testid={`switch-region-${r.id}`}
              >
                <span className="text-lg">{r.flag}</span>
                <span>{r.label}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
